import {createSelector} from 'reselect';
import isempty from 'lodash/isEmpty';
import {getTranslations, getListingPage} from './store';
import {labelMaker} from '../../server/utils/labelMaker';
import {log as logToServer} from '../api/logger';
import {priceErrorMap} from '../utils/errorEvents';
import {getPriceDetails} from './priceDetails';
import {getSleeps} from './listing';

// This is the priceDetails from the graphql-traveler-checkout partials.
// This note is important until we remove all the PDS related details.
const getCheckoutError = createSelector(
    [getListingPage],
    (listingPage) => {
        const priceDetails =
            listingPage.priceDetails && listingPage.priceDetails.toJS
                ? listingPage.priceDetails.toJS()
                : listingPage.priceDetails || {};
        return priceDetails && priceDetails.originalErrorCode
            ? {errorCode: priceDetails.originalErrorCode}
            : null;
    }
);

const getErrorCode = createSelector(
    [getCheckoutError],
    (checkoutError) => {
        return (!!checkoutError && checkoutError.errorCode) || null;
    }
);

const getErrorObj = createSelector(
    [getErrorCode],
    (errorCode) => {
        let error;

        if (errorCode) {
            error = priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;
        }

        if (errorCode && !priceErrorMap[errorCode]) {
            logToServer({
                message: 'unhandled error',
                errorCode
            });
        }

        return error || null;
    }
);

const getErrorMessage = createSelector(
    [getErrorObj, getCheckoutError, getTranslations],
    (errorObj, checkoutError, translations) => {
        return (
            (!!errorObj && translations[errorObj.translationKey]) ||
            (!!checkoutError && checkoutError.message) ||
            null
        );
    }
);

const getLabelWithData = createSelector(
    [getErrorObj, getErrorCode, getSleeps, getTranslations],
    (errorObj, errorCode, sleeps, translations) => {
        if (!!errorObj && errorObj.requiresDataForTranslation) {
            // for now, there's only one message we're handling that needs this, too many guests.
            // if/when more come along, we'll have to refactor this to handle the data the others need.
            return Object.assign({}, errorObj, {
                label: labelMaker(translations[errorObj.translationKey], sleeps)
            });
        }

        return null;
    }
);

const getPriceError = createSelector(
    [getErrorObj, getErrorCode, getLabelWithData, getErrorMessage],
    (errorObj, errorCode, labelWithData, errorMessage) => {
        if (labelWithData) {
            return labelWithData;
        }

        if (errorMessage) {
            return Object.assign({}, errorObj, {
                label: errorMessage
            });
        }

        return null;
    }
);

const getHasPriceError = createSelector(
    [getCheckoutError],
    (checkoutError) => !!checkoutError
);

const getAvailable = createSelector(
    [getPriceDetails, getCheckoutError],
    (priceDetails, checkoutError) => {
        return !isempty(priceDetails) && !checkoutError;
    }
);

const getPriceStatus = createSelector(
    [getAvailable, getHasPriceError, getPriceError],
    (isAvailable, hasPriceError, priceError) => {
        return {
            isAvailable,
            hasPriceError,
            priceError
        };
    }
);

export default getPriceStatus;
export {
    getAvailable,
    getErrorObj,
    getErrorCode,
    getErrorMessage,
    getLabelWithData,
    getCheckoutError,
    getPriceError,
    getPriceStatus
};
